// Dane raw z providera, zgodnie z opisem w RestApi::getEntries()
import type { BookingProvider } from './provider';
import { appendProviderQuery } from './provider';

export interface ServicePricing {
  default: string;
  weekend: string;
  special_dates: string[];
}

export interface RawUser {
  uuid: string;
  name: string;
  lastname: string;
  medicalPwz?: string;
  phone?: string;
  color?: string;
  specializations?: Array<{
    id: number;
    name: string;
  }>;
  photo?: string;
  description?: string;
  patientGroup?: 'children' | 'adults';
  serviceOverrides?: Array<{
    uuid: string;
    price: string;
    pricing?: ServicePricing;
  }>;
  defaultServiceUuid?: string | null;
  [key: string]: unknown;
}

export interface RawService {
  uuid: string;
  name: string;
  description?: string;
  duration: number;
  language?: string;
  break?: number;
  categoryId?: number;
  price: string;
  pricing?: ServicePricing;
  [key: string]: unknown;
}

export interface EntriesResponse {
  mode: 'service' | 'practitioner' | 'mixed' | 'all';
  entries: {
    users: RawUser[];
    services: RawService[];
  };
}

export interface AvailabilityResponse {
  slots: Record<string, string[]>;
}

export type PractitionerEntry = RawUser;
export type ServiceEntry = RawService;

export interface OrderPayload {
  chosen_user: string;
  chosen_service: string;
  chosen_date: string;
  email: string;
  phone: string;
  patient_name: string;
  patient_lastname: string;
  birth_date?: string;
  visit_language?: string;
  patient_notes?: string;
  page_url?: string;
  user_name?: string;
  service_name?: string;
}

export async function fetchEntries(
  restUrl: string,
  users: string[],
  services: string[],
  provider: BookingProvider = 'medfile'
): Promise<EntriesResponse> {
  const params = new URLSearchParams();
  params.set('provider', provider);
  if (users.length) {
    params.set('users', users.join(','));
  }
  if (services.length) {
    params.set('services', services.join(','));
  }

  const res = await fetch(`${restUrl}/entries?${params.toString()}`, {
    credentials: 'same-origin',
  });
  if (!res.ok) {
    throw new Error(`entries http ${res.status}`);
  }
  return (await res.json()) as EntriesResponse;
}

export async function fetchAvailability(
  restUrl: string,
  userUuid: string,
  serviceUuid: string,
  startYmd: string,
  endYmd: string,
  provider: BookingProvider = 'medfile',
  signal?: AbortSignal
): Promise<AvailabilityResponse> {
  const params = new URLSearchParams();
  params.set('provider', provider);
  params.set('user_uuid', userUuid);
  params.set('service_uuid', serviceUuid);
  params.set('start_date', startYmd);
  params.set('end_date', endYmd);

  const res = await fetch(`${restUrl}/availability?${params.toString()}`, {
    credentials: 'same-origin',
    signal,
  });
  if (!res.ok) {
    throw new Error(`availability http ${res.status}`);
  }
  return (await res.json()) as AvailabilityResponse;
}

export async function fetchServicesByUser(
  restUrl: string,
  userUuid: string,
  provider: BookingProvider = 'medfile'
): Promise<{ services: RawService[] }> {
  const params = new URLSearchParams();
  params.set('provider', provider);
  params.set('user_uuid', userUuid);

  const res = await fetch(`${restUrl}/services?${params.toString()}`, {
    credentials: 'same-origin',
  });

  if (!res.ok) {
    throw new Error(`services http ${res.status}`);
  }

  return (await res.json()) as { services: RawService[] };
}

export interface OrderResult {
  success: boolean;
  message?: string;
  medfileError?: boolean;
  providerError?: boolean;
  errorHtml?: string;
}

export async function postOrder(
  restUrl: string,
  payload: OrderPayload,
  provider: BookingProvider = 'medfile'
): Promise<OrderResult> {
  const res = await fetch(appendProviderQuery(`${restUrl}/order`, provider), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.success) {
    return {
      success: false,
      message: data.message || 'Nie udało się zapisać. Spróbuj ponownie.',
      medfileError: !!(data.medfile_error || data.provider_error),
      providerError: !!data.provider_error,
      errorHtml: data.error_html || '',
    };
  }

  return { success: true };
}
